import {
  createLastNWindow,
  DataPointsApiResponse,
  DataRow,
  DataTable,
  dateTruncFromGranularity,
  defaultDateWindow,
  getDefinitionWithServerOrDefaultWindow,
  getMeasureFormatString,
  getTimestampGranularity,
  getWarehousedDefinitionWithDayConfig,
  materializeTable,
  maxPrecision,
  parseDataPoints} from '@tableau/metrics-engine';
import { FeatureFlags as MetricsFeatureFlags, MetricCardProps } from '@tableau/metrics-shared-widgets-dom';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';

import { IMetricInfo } from './models/IMetricInfo';
import { IMetricDataList } from './serverApi/types/IMetricDataList';

export const metricCardProps = (metric: DataPointsApiResponse): MetricCardProps => {
  const data = metric.data ? metric.data : [];
  const measureFormat = getMeasureFormatString(metric.definition);

  const defaultWindow = defaultDateWindow(dateTruncFromGranularity(getTimestampGranularity(metric.definition)));
  const definitionWithDefaultWindow = getDefinitionWithServerOrDefaultWindow(metric.definition, defaultWindow, FeatureFlags.metricsDateWindowEnabled);
  const definition = getWarehousedDefinitionWithDayConfig(definitionWithDefaultWindow);

  const materializedData = materializeTable(definition, data);
  const dataToRender = materializedData.unwrapOr([]);

  const metricsReferenceLineEnabled = FeatureFlags.metricsReferenceLineEnabled;

  return {
    testId: 'metric-card',
    data: dataToRender,
    definition,
    lastUpdatedTimestamp: metric.dataRefreshedTimestamp,
    measureFormat,
    suspended: false,
    metricColorIndicatorsEnabled: FeatureFlags.metricsColorIndicatorsEnabled,
    metricsReferenceLineEnabled,
  };
};

export const getTableWithDefaultComparison = (metric: DataPointsApiResponse): DataTable => {
  const data = metric.data ? metric.data : [];
  const definition = getDefinitionWithServerOrDefaultWindow(metric.definition, createLastNWindow(100), FeatureFlags.metricsDateWindowEnabled);

  const materializedData = materializeTable(definition, data);
  return materializedData.unwrapOr([]);
};

export const getMergedMetricInfo = (metricInfo: IMetricInfo, metricDataList: IMetricDataList): IMetricInfo => {
  const data = metricDataList.data[metricInfo.id];
  if (data) {
    return { ...metricInfo,
      dataPoints: parseDataPoints(data.points, maxPrecision(data.schema?.measure?.format)),
      schema: data.schema,
    dataRefreshedTimestamp: data.dataRefreshedTimestamp };
  } else {
    return metricInfo;
  }
};

export const getMetricMeasure = (metric: DataPointsApiResponse | undefined, measureAccessor: (point: DataRow) => number): number | undefined => {
  return metric?.data?.length > 0 && measureAccessor(metric.data[metric.data.length - 1]);
};

export const metricsFeatureFlagsAdapter = (): MetricsFeatureFlags => {
  return {
      metricsDateWindowEnabled: FeatureFlags.metricsDateWindowEnabled,
      metricsReferenceLineEnabled: FeatureFlags.metricsReferenceLineEnabled,
      metricsColorIndicatorsEnabled: FeatureFlags.metricsColorIndicatorsEnabled,
      metricsHistoricalComparisonsEnabled: FeatureFlags.metricsHistoricalComparisonsEnabled,
      metricsWindowSwitcherEnabled: FeatureFlags.metricsEditingEnabled,
    };
};
